import styled from 'styled-components';

import { NfzDepartment, NfzDepartmentQuery } from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { commonTranslationKeys, createTranslatedText, FieldController, TranslatableText } from '@dreryk/react-common';
import {
   FormController,
   FormLabel,
   resolveInputLayoutStyles,
   Select,
   StandardSpinner,
   useSelectContext,
   ValidationErrorNotification,
} from '@dreryk/react-components';

import { createNfzDepartmentSelectFilter } from '../../../../../common/common.utils';
import { draftPrescriptionsTranslationKeys } from '../../../../../translations';

export const useCqrsSearchNfzDepartmentList = (): UseCqrsQueryResult<NfzDepartment[]> => {
   return useCqrsSearch(
      () => new NfzDepartmentQuery(),
      paginations.all<NfzDepartment>().orderedBy('code', paginations.Direction.ASCENDING)
   );
};

type NfzDepartmentSelectProps = {
   readonly label: TranslatableText;
   readonly controller: FieldController<NfzDepartment | null>;
};

const getDepartmentLabel = (currentValue?: NfzDepartment): string => `${currentValue!.name} (${currentValue!.code})`;

const StyledNfzDepartmentSelect = styled.div`
   ${resolveInputLayoutStyles};
`;

StyledNfzDepartmentSelect.displayName = 'StyledNfzDepartmentSelect';

type NfzDepartmentSelectListProps = {
   readonly departments: NfzDepartment[];
};
const NfzDepartmentSelectList = (props: NfzDepartmentSelectListProps): JSX.Element => {
   const { searchText } = useSelectContext();
   return (
      <Select.List<NfzDepartment>
         items={props.departments.filter(createNfzDepartmentSelectFilter(searchText))}
         keyExtractor={'code'}
      >
         {(department) => (
            <Select.Item
               testId={department.code}
               item={department}
               label={<Select.Highlight node={createTranslatedText(getDepartmentLabel(department))} />}
            />
         )}
      </Select.List>
   );
};

export const NfzDepartmentSelect = (props: NfzDepartmentSelectProps): JSX.Element => {
   const {
      data: nfzDepartments,
      fetching: nfzDepartmentsFetching,
      error: nfzDepartmentsError,
   } = useCqrsSearchNfzDepartmentList();

   if (!nfzDepartments?.length) {
      return (
         <StyledNfzDepartmentSelect>
            <FormLabel label={props.label} testId={'nfzDepartment'} />
            {nfzDepartmentsFetching ? (
               <StandardSpinner text={draftPrescriptionsTranslationKeys.fetchingNfzDepartments} />
            ) : null}
            {nfzDepartmentsError ? (
               <ValidationErrorNotification
                  testId={'nfzDepartment'}
                  label={draftPrescriptionsTranslationKeys.failedToFetchNfzDepartments}
               />
            ) : null}
            <FormController controller={props.controller} />
         </StyledNfzDepartmentSelect>
      );
   }

   return (
      <Select<NfzDepartment>
         controller={props.controller}
         label={props.label}
         triggerLabelExtractor={getDepartmentLabel}
         placeholder={commonTranslationKeys.choose}
         testId={'nfzDepartment'}
         searchInputEnabled
      >
         <NfzDepartmentSelectList departments={nfzDepartments} />
      </Select>
   );
};
